import { z } from "zod";

/**
 * Regex for a valid order item code.
 * Format: SXXXXXXX-X
 *   - S       : literal "S" prefix
 *   - XXXXXXX : exactly 7 numeric digits (item code)
 *   - -       : literal dash separator
 *   - X+      : one or more numeric digits (item index)
 *
 * Examples: S1234567-1, S0000001-12
 */
const ORDER_ITEM_CODE_REGEX = /^S\d{7}-\d+$/;

const OrderItemCodeSchema = z
  .string()
  .regex(ORDER_ITEM_CODE_REGEX, "Invalid order item code format. Expected SXXXXXXX-X");

export class OrderItemCodeValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "OrderItemCodeValidationError";
  }
}

export class OrderItemCodeVo {
  readonly itemCode: string;
  readonly index: number;

  private constructor(readonly value: string) {
    const [itemCode, indexStr] = value.split("-");
    this.itemCode = itemCode;
    this.index = parseInt(indexStr, 10);
  }

  static of(value: string): OrderItemCodeVo {
    const result = OrderItemCodeSchema.safeParse(value);
    if (!result.success) {
      throw new OrderItemCodeValidationError(
        result.error.issues[0]?.message ?? "Invalid order item code"
      );
    }
    return new OrderItemCodeVo(result.data);
  }

  static isValid(value: string): boolean {
    return ORDER_ITEM_CODE_REGEX.test(value);
  }

  toString(): string {
    return this.value;
  }
}
